import React, { useState, useEffect } from 'react';
import { Layout, Menu, Typography, theme } from 'antd';
import { UserOutlined, ShopOutlined, PrinterOutlined, CloudSyncOutlined, FieldTimeOutlined, PercentageOutlined } from '@ant-design/icons';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';

const { Title } = Typography;
const { Sider, Content } = Layout;

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        console.error("Settings Error:", error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div style={{ padding: 24, paddingLeft: 200, color: 'red' }}>
                    <h2>Settings Component Error</h2>
                    <p>Something went wrong loading this section. Please try refreshing the page.</p>
                    <div style={{ background: '#fff1f0', padding: 16, border: '1px solid #ffa39e', borderRadius: 4, overflow: 'auto' }}>
                        <strong>Details:</strong>
                        <pre style={{ marginTop: 8 }}>{this.state.error && this.state.error.toString()}</pre>
                        {this.state.error && this.state.error.stack && (
                            <details style={{ marginTop: 8 }}>
                                <summary>Technical Stack Trace</summary>
                                <pre style={{ fontSize: 10, marginTop: 8 }}>{this.state.error.stack}</pre>
                            </details>
                        )}
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

const SettingsLayout = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // Determine active key from path
    const getActiveKey = () => {
        if (location.pathname.includes('/settings/invoice')) return 'invoice';
        if (location.pathname.includes('/settings/users')) return 'users';
        if (location.pathname.includes('/settings/backups')) return 'backups';
        if (location.pathname.includes('/settings/time')) return 'time';
        if (location.pathname.includes('/settings/tax-profiles')) return 'tax-profiles';
        return 'company';
    };

    const [activeKey, setActiveKey] = useState(getActiveKey());

    useEffect(() => {
        setActiveKey(getActiveKey());
    }, [location.pathname]);

    const handleMenuClick = (e) => {
        setActiveKey(e.key);
        switch (e.key) {
            case 'company':
                navigate('/settings/company');
                break;
            case 'invoice':
                navigate('/settings/invoice');
                break;
            case 'users':
                navigate('/settings/users');
                break;
            case 'backups':
                navigate('/settings/backups');
                break;
            case 'time':
                navigate('/settings/time');
                break;
            case 'tax-profiles':
                navigate('/settings/tax-profiles');
                break;
            default:
                navigate('/settings/company');
        }
    };

    const menuItems = [
        { key: 'company', icon: <ShopOutlined />, label: 'Company Info' },
        { key: 'invoice', icon: <PrinterOutlined />, label: 'Invoice Settings' },
        { key: 'users', icon: <UserOutlined />, label: 'Users & Permissions' },
        { key: 'backups', icon: <CloudSyncOutlined />, label: 'System Backups' },
        { key: 'time', icon: <FieldTimeOutlined />, label: 'Time & Date' },
        { key: 'tax-profiles', icon: <PercentageOutlined />, label: 'Tax Profiles' },
    ];

    const {
        token: { colorBgContainer },
    } = theme.useToken();

    return (
        <Layout style={{ minHeight: 'calc(100vh - 64px)', background: 'transparent' }}>
            <Sider
                width={250}
                theme="light"
                style={{
                    background: colorBgContainer,
                    borderRight: '1px solid #f0f0f0'
                }}
            >
                <div style={{ padding: '16px 24px' }}>
                    <Title level={4} style={{ margin: 0 }}>Settings</Title>
                    <Typography.Text type="secondary">System Configuration</Typography.Text>
                </div>
                <Menu
                    mode="inline"
                    selectedKeys={[activeKey]}
                    onClick={handleMenuClick}
                    items={menuItems}
                    style={{ borderRight: 0 }}
                />
            </Sider>
            <Content style={{ padding: '24px', maxWidth: 1200, margin: '0 auto', width: '100%' }}>
                <Outlet />
            </Content>
        </Layout>
    );
};

export default function SettingsWithErrorBoundary() {
    return (
        <ErrorBoundary>
            <SettingsLayout />
        </ErrorBoundary>
    );
}
